"use client";

import Image from "next/image";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  ArrowRight,
  Briefcase,
  Check,
  ClipboardList,
  GraduationCap,
  Layers,
  Megaphone,
  Plane,
  Play,
  ShieldCheck,
  Star,
  User,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import { Link } from "@/i18n/navigation";
import { Button } from "@/components/ui/button";
import { reactWebAuthHref } from "@/lib/reactWebAuthUrl";
import type {
  HomeHeroAgent,
  HomeHeroChipTone,
  HomeHeroCluster,
  HomeHeroIconKey,
  HomeHeroPanel,
  HomeHeroPanelRow,
} from "@/types/home-messages";

const NAVY = "#1e3a5f";
const ORANGE = "#FF6B35";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeToReducedMotion(onStoreChange: () => void): () => void {
  const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  mediaQuery.addEventListener("change", onStoreChange);
  return () => mediaQuery.removeEventListener("change", onStoreChange);
}

function reducedMotionSnapshot(): boolean {
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

function reducedMotionServerSnapshot(): boolean {
  return false;
}

const iconMap: Record<HomeHeroIconKey, LucideIcon> = {
  plane: Plane,
  users: Users,
  briefcase: Briefcase,
  user: User,
  clipboard: ClipboardList,
  megaphone: Megaphone,
  shield: ShieldCheck,
  wrench: Wrench,
  graduation: GraduationCap,
  star: Star,
  layers: Layers,
};

const chipStyles: Record<HomeHeroChipTone, { bg: string; fg: string }> = {
  green: { bg: "#dcfce7", fg: "#15803d" },
  amber: { bg: "#fef3c7", fg: "#b45309" },
  red: { bg: "#fee2e2", fg: "#b91c1c" },
  blue: { bg: "#dbeafe", fg: "#1d4ed8" },
  navy: { bg: "#e0e7ef", fg: NAVY },
  gray: { bg: "#f1f5f9", fg: "#475569" },
};

function Chip({
  tone,
  children,
}: {
  readonly tone: HomeHeroChipTone;
  readonly children: string;
}) {
  const styles = chipStyles[tone];

  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{ backgroundColor: styles.bg, color: styles.fg }}
    >
      {children}
    </span>
  );
}

function ProgressBar({
  pct,
  color = "#22c55e",
}: {
  readonly pct: number;
  readonly color?: string;
}) {
  const safePct = Math.max(0, Math.min(100, pct));

  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
      <div
        className="h-full rounded-full"
        style={{ width: `${safePct}%`, backgroundColor: color }}
      />
    </div>
  );
}

function PanelRow({ row }: { readonly row: HomeHeroPanelRow }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2 text-[11px]">
        <span className="flex min-w-0 items-center gap-2 text-gray-800">
          {row.checked ? (
            <Check
              className="h-3 w-3 shrink-0 text-green-600"
              strokeWidth={3}
              aria-hidden
            />
          ) : null}
          <span className="truncate">{row.label}</span>
        </span>
        {row.chip && row.tone ? <Chip tone={row.tone}>{row.chip}</Chip> : null}
        {row.value ? (
          <span className="shrink-0 font-medium text-gray-900">
            {row.value}
          </span>
        ) : null}
        {row.arrow ? (
          <ArrowRight
            className="h-3 w-3 shrink-0"
            style={{ color: ORANGE }}
            aria-hidden
          />
        ) : null}
      </div>
      {typeof row.progress === "number" ? (
        <ProgressBar pct={row.progress} color={row.progressColor} />
      ) : null}
    </div>
  );
}

function AgentPanel({ panel }: { readonly panel: HomeHeroPanel }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
      <div
        className="mb-2 text-[10px] font-bold uppercase tracking-normal"
        style={{ color: NAVY }}
      >
        {panel.title}
      </div>

      {panel.profile ? (
        <>
          <div className="mb-2 flex items-center gap-2">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
              style={{ backgroundColor: NAVY }}
            >
              {panel.profile.initials}
            </div>
            <div className="min-w-0">
              <div
                className="truncate text-[12px] font-bold"
                style={{ color: NAVY }}
              >
                {panel.profile.name}
              </div>
              <div className="truncate text-[10px] text-gray-500">
                {panel.profile.role}
              </div>
            </div>
          </div>
          <div className="mb-1 flex items-center justify-between gap-2 text-[10px]">
            <span className="text-gray-600">
              {panel.profile.completionLabel}
            </span>
            <span className="font-semibold text-green-700">
              {panel.profile.completionValue}
            </span>
          </div>
          <ProgressBar pct={panel.profile.completionProgress} />
        </>
      ) : null}

      {panel.media ? (
        <div className="mb-2">
          <div className="mb-2 flex h-16 items-center justify-center overflow-hidden rounded-md bg-slate-800">
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 shadow"
              aria-label={panel.media.playLabel}
            >
              <Play
                className="ml-0.5 h-3.5 w-3.5"
                style={{ color: ORANGE }}
                fill={ORANGE}
                aria-hidden
              />
            </button>
          </div>
          <div className="text-[11px] font-semibold" style={{ color: NAVY }}>
            {panel.media.title}
          </div>
          <div className="text-[10px] text-gray-500">
            {panel.media.duration}
          </div>
        </div>
      ) : null}

      {panel.metric ? (
        <div className="mb-2 flex items-baseline gap-2">
          <span className="text-[18px] font-bold" style={{ color: NAVY }}>
            {panel.metric.value}
          </span>
          <Chip tone={panel.metric.tone}>{panel.metric.chip}</Chip>
        </div>
      ) : null}

      {panel.rows?.length ? (
        <div className="space-y-2">
          {panel.rows.map((row) => (
            <PanelRow key={`${panel.title}-${row.label}`} row={row} />
          ))}
        </div>
      ) : null}

      {panel.footnote ? (
        <div className="pt-1 text-[10px] text-gray-500">{panel.footnote}</div>
      ) : null}
    </div>
  );
}

export default function HeroSection() {
  const t = useTranslations("Hero");
  const locale = useLocale();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = useSyncExternalStore(
    subscribeToReducedMotion,
    reducedMotionSnapshot,
    reducedMotionServerSnapshot,
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clusters = (t.raw("clusters") as HomeHeroCluster[]) ?? [];
  const agents = (t.raw("agents") as HomeHeroAgent[]) ?? [];
  const partnerTools = (t.raw("partnerTools") as string[]) ?? [];

  useEffect(() => {
    if (reduced || paused || agents.length <= 1) return undefined;
    timerRef.current = setTimeout(
      () => setActive((i) => (i + 1) % agents.length),
      4000,
    );
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [active, agents.length, paused, reduced]);

  if (agents.length === 0) return null;

  const currentIndex = agents[active] ? active : 0;
  const agent = agents[currentIndex];
  const AgentIcon = iconMap[agent.icon as HomeHeroIconKey] ?? ClipboardList;
  const signupHref = reactWebAuthHref(locale, "signup");

  const jumpTo = (index: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setActive(index);
  };

  return (
    <section className="relative z-10 bg-gradient-to-b from-white via-slate-50/40 to-white px-4 pb-10 pt-8 sm:px-6 lg:px-8">
      <div className="relative mx-auto max-w-7xl">
        <div className="pointer-events-none absolute left-[40%] top-0 z-30 hidden -translate-x-1/2 lg:block">
          <motion.div
            animate={reduced ? undefined : { y: [0, -8, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <Image
              src={t("droneImageSrc")}
              alt={t("droneImageAlt")}
              width={208}
              height={139}
              className="h-auto w-[208px] select-none"
              priority
            />
          </motion.div>
        </div>

        <svg
          className="pointer-events-none absolute left-[36%] top-[50px] z-40 hidden h-[280px] w-[16%] lg:block"
          viewBox="0 0 200 280"
          fill="none"
          aria-hidden
          preserveAspectRatio="none"
        >
          <path
            d="M 2 278 C 40 280 80 275 110 250 C 128 232 130 210 126 190 C 122 140 95 130 80 148 C 65 166 85 190 112 178 C 145 165 180 140 192 100 C 200 70 195 45 160 16"
            stroke={ORANGE}
            strokeWidth="2.5"
            strokeDasharray="6 7"
            strokeLinecap="round"
          />
        </svg>

        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-10">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[12px] text-gray-700 shadow-sm">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: ORANGE }}
                aria-hidden
              />
              {t("eyebrow")}
            </div>

            <h1
              className="mt-4 text-[40px] font-bold leading-[0.95] tracking-normal min-[380px]:text-[48px] sm:text-[64px] lg:text-[76px]"
              style={{ color: NAVY }}
            >
              <span className="block">{t("titlePlan")}</span>
              <span className="block">{t("titleTrack")}</span>
              <span className="block">{t("titleFly")}</span>
              <span className="block break-words" style={{ color: ORANGE }}>
                {t("titleAgents")}
              </span>
            </h1>

            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-gray-600">
              {t("intro")}
            </p>

            <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {clusters.map((cluster) => {
                const ClusterIcon = iconMap[cluster.icon] ?? Plane;
                return (
                  <div
                    key={cluster.label}
                    className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
                  >
                    <div className="mb-2 flex items-center gap-1.5">
                      <ClusterIcon
                        className="h-3.5 w-3.5"
                        style={{ color: ORANGE }}
                        aria-hidden
                      />
                      <span
                        className="text-[10px] font-semibold uppercase tracking-normal"
                        style={{ color: NAVY }}
                      >
                        {cluster.label}
                      </span>
                    </div>
                    <ul className="space-y-1 pl-0">
                      {cluster.items.map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-1.5 text-[11px] text-gray-700"
                        >
                          <Check
                            className="mt-0.5 h-3 w-3 shrink-0"
                            style={{ color: ORANGE }}
                            strokeWidth={3}
                            aria-hidden
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-5">
              <div className="relative">
                <div
                  className="absolute -inset-0.5 rounded-lg opacity-70 blur-sm"
                  style={{ backgroundColor: ORANGE }}
                  aria-hidden
                />
                <Button
                  asChild
                  className="relative h-12 border-0 px-6 text-white hover:opacity-95"
                  style={{ backgroundColor: NAVY }}
                >
                  <a href={signupHref}>
                    {t("primaryCta")}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </a>
                </Button>
              </div>
              <Link
                href={t("businessHref")}
                className="flex items-center gap-1 text-[14px] font-medium hover:underline"
                style={{ color: ORANGE }}
              >
                {t("businessCta")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>

            <div className="mt-6">
              <p className="mb-2 max-w-xl text-[11px] leading-relaxed text-gray-500">
                {t("partnerToolsLabel")}
              </p>
              <div className="flex flex-wrap items-center gap-5 text-gray-400">
                {partnerTools.map((tool) => (
                  <span
                    key={tool}
                    className="text-[13px] font-semibold tracking-normal"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="relative">
            <div
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              className="relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.12)]"
            >
              <AnimatePresence initial={false} mode="wait">
                <motion.div
                  key={agent.key}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.45 }}
                  className="relative h-[520px] overflow-hidden pb-4 sm:h-[576px]"
                >
                  <div className="pointer-events-none absolute bottom-0 right-0 z-0 h-[92%] w-[70%] bg-white opacity-35 sm:bottom-3 sm:right-3 sm:h-[94%] sm:w-[58%] sm:opacity-100">
                    <img
                      src={agent.imageSrc}
                      alt={agent.imageAlt}
                      className="h-full w-full select-none object-contain object-right-bottom"
                      draggable={false}
                      style={{
                        filter:
                          agent.key === "pm"
                            ? "brightness(1.08) contrast(0.92)"
                            : undefined,
                      }}
                    />
                  </div>

                  <div className="absolute right-5 top-5 z-30">
                    <Chip tone={agent.badgeTone}>{agent.badgeText}</Chip>
                  </div>

                  <div className="relative z-10 flex h-full flex-col p-5">
                    <div className="flex min-w-0 items-start gap-3 pr-4 sm:max-w-[58%]">
                      <div
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full shadow-md"
                        style={{ backgroundColor: NAVY }}
                      >
                        <AgentIcon className="h-5 w-5 text-white" aria-hidden />
                      </div>
                      <div className="min-w-0">
                        <div
                          className="text-[26px] font-bold leading-none"
                          style={{ color: NAVY }}
                        >
                          {agent.short}
                        </div>
                        <div
                          className="mt-1 text-[11.5px] font-semibold"
                          style={{ color: ORANGE }}
                        >
                          {agent.name}
                        </div>
                      </div>
                    </div>

                    <div className="max-w-full sm:max-w-[58%]">
                      <p className="mt-3 text-[11.5px] leading-relaxed text-gray-600">
                        {agent.description}
                      </p>
                      <ul className="mt-2 space-y-1 pl-0">
                        {agent.checks.map((check) => (
                          <li
                            key={check}
                            className="flex items-start gap-2 text-[11px] text-gray-800"
                          >
                            <Check
                              className="mt-0.5 h-3 w-3 shrink-0"
                              style={{ color: ORANGE }}
                              strokeWidth={3}
                              aria-hidden
                            />
                            <span>{check}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="relative z-20 mt-auto flex w-full flex-col gap-2 pt-3 sm:w-[52%]">
                      {agent.panels.slice(0, 2).map((panel, index) => (
                        <div
                          key={`${agent.key}-${panel.title}`}
                          className={index === 1 ? "w-full sm:pl-6" : "w-full"}
                        >
                          <AgentPanel panel={panel} />
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-5">
              <p className="mb-3 text-center text-[12px] text-gray-500">
                {t("agentRailLabel")}
              </p>
              <div className="flex items-start justify-between gap-2">
                {agents.map((item, index) => {
                  const isActive = index === currentIndex;
                  const RailIcon =
                    iconMap[item.icon as HomeHeroIconKey] ?? ClipboardList;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => jumpTo(index)}
                      className="group flex flex-1 flex-col items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                      aria-pressed={isActive}
                    >
                      <div
                        className="flex h-12 w-12 items-center justify-center rounded-full border bg-white transition-all"
                        style={
                          isActive
                            ? {
                                borderColor: ORANGE,
                                boxShadow: `0 0 0 3px ${ORANGE}33, 0 6px 16px rgba(0,0,0,0.08)`,
                              }
                            : { borderColor: "#e5e7eb" }
                        }
                      >
                        <RailIcon
                          className="h-5 w-5"
                          style={{ color: isActive ? ORANGE : NAVY }}
                          aria-hidden
                        />
                      </div>
                      <span className="text-center">
                        <span
                          className="block text-[11px] font-bold leading-tight"
                          style={{ color: NAVY }}
                        >
                          {item.short}
                        </span>
                        <span className="mx-auto block max-w-[100px] text-[9.5px] leading-tight text-gray-500">
                          {item.name}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
